import { useEffect, useState } from 'react';

/**
 * Hook to manage Reviews.io widget script loading
 * Ensures the script is loaded before attempting to use the widget
 */
export function useReviewsIoScript(): {
    isLoaded: boolean;
    isError: boolean;
} {
    const [isLoaded, setIsLoaded] = useState(false);
    const [isError, setIsError] = useState(false);

    useEffect(() => {
        // Check if ReviewsWidget is already available
        if (typeof window !== 'undefined' && window.ReviewsWidget) {
            setIsLoaded(true);
            return;
        }

        let script: HTMLScriptElement | null = null;
        let checkInterval: NodeJS.Timeout | null = null;
        let timeoutId: NodeJS.Timeout | null = null;

        const checkForWidget = () => {
            if (typeof window !== 'undefined' && window.ReviewsWidget) {
                setIsLoaded(true);
                if (checkInterval) {
                    clearInterval(checkInterval);
                    checkInterval = null;
                }
                if (timeoutId) {
                    clearTimeout(timeoutId);
                    timeoutId = null;
                }
                return true;
            }
            return false;
        };

        // First, check if the widget is already available
        if (checkForWidget()) {
            return;
        }

        // Check if script is already in the DOM
        const existingScript = document.querySelector(
            'script[src*="reviews.io"], script[src*="reviewscouk"], script[src*="widget.reviews.io"]'
        ) as HTMLScriptElement;

        if (existingScript) {
            // Script exists, wait for it to load
            if (existingScript.dataset.loaded === 'true') {
                // Already loaded, just wait for widget to be available
                checkInterval = setInterval(checkForWidget, 100);
            } else {
                // Listen for load event
                existingScript.addEventListener('load', () => {
                    existingScript.dataset.loaded = 'true';
                    checkInterval = setInterval(checkForWidget, 100);
                });

                existingScript.addEventListener('error', () => {
                    setIsError(true);
                });

                // Also start checking immediately in case it loaded before we attached listeners
                checkInterval = setInterval(checkForWidget, 100);
            }
        } else {
            // Create and load the script
            script = document.createElement('script');
            script.src = 'https://widget.reviews.io/polaris/build.js';
            script.async = true;
            script.dataset.loaded = 'false';

            script.onload = () => {
                script!.dataset.loaded = 'true';
                // Start checking for widget availability
                checkInterval = setInterval(checkForWidget, 100);
            };

            script.onerror = () => {
                setIsError(true);
                console.error('Failed to load Reviews.io widget script');
            };

            document.head.appendChild(script);
        }

        // Set a timeout to stop checking after 30 seconds
        timeoutId = setTimeout(() => {
            if (checkInterval) {
                clearInterval(checkInterval);
                checkInterval = null;
            }
            if (!isLoaded) {
                setIsError(true);
                console.error('Timeout waiting for Reviews.io widget to load');
            }
        }, 30000);

        // Cleanup
        return () => {
            if (checkInterval) {
                clearInterval(checkInterval);
            }
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
            // Note: We don't remove the script as it might be used elsewhere
        };
    }, []);

    return { isLoaded, isError };
}
